import { Avatar } from '@assembly-js/design-system';
import { InputAdornment } from '@material-ui/core';
import { AutocompleteRenderInputParams } from '@material-ui/lab';
import { FormikErrors, FormikTouched } from 'formik';
import React from 'react';

import { TextField } from 'src/components/TextField';
import { Company } from 'src/store/clients/types';
import { getInitials } from 'src/utils/UserUtils';

import { CompanyAutocompleteItem } from './types';

type RenderInputProps<Fields extends Record<string, unknown>> =
  AutocompleteRenderInputParams & {
    autoFocus: boolean | undefined;
    errors: FormikErrors<Fields> | undefined;
    inputValue: string;
    isSelectedCompanyIdUuid: boolean;
    label: React.ReactNode;
    onBlur: (() => void) | undefined;
    onChange: (item: CompanyAutocompleteItem | null) => void;
    onKeyDown: ((e: React.KeyboardEvent<HTMLDivElement>) => void) | undefined;
    placeholder: string | undefined;
    selectedCompany: Company | undefined;
    selectedCompanyId: string | undefined;
    setInputValue: (value: string) => void;
    setValue: (item: CompanyAutocompleteItem | null) => void;
    touched: FormikTouched<Fields> | undefined;
  };

export function SelectCompanyTextInput<Fields extends Record<string, unknown>>({
  autoFocus,
  errors,
  inputValue,
  isSelectedCompanyIdUuid,
  label,
  onBlur,
  onChange,
  onKeyDown,
  placeholder,
  selectedCompany,
  selectedCompanyId,
  setInputValue,
  setValue,
  touched,
  ...params
}: RenderInputProps<Fields>) {
  const isCompany = selectedCompany && isSelectedCompanyIdUuid;
  const isPlaceholderName = Boolean(
    !selectedCompany && !isSelectedCompanyIdUuid && selectedCompanyId,
  );

  params.InputProps.startAdornment =
    isCompany || isPlaceholderName ? (
      <InputAdornment className="mr-1 ml-1" position="start">
        <div>
          <Avatar
            size="xs"
            variant="rounded"
            src={selectedCompany?.fields.avatarImageURL ?? ''}
            fallbackColor={selectedCompany?.fields.fallbackColor}
            text={getInitials(
              selectedCompany?.fields.name ?? selectedCompanyId,
            )}
            alt={selectedCompany?.fields.name ?? selectedCompanyId ?? ''}
          />
        </div>
      </InputAdornment>
    ) : (
      params.InputProps.startAdornment
    );

  return (
    <TextField
      {...params}
      autoFocus={autoFocus}
      autoComplete="off"
      placeholder={placeholder}
      error={
        touched && errors
          ? Boolean(touched.companyId && errors.companyId)
          : false
      }
      fullWidth
      helperText={
        touched && errors ? touched.companyId && errors.companyId : ' '
      }
      id="company-name-input"
      inputProps={{
        ...params.inputProps,
        autoComplete: 'company-name-input',
        isautocomplete: 'true',
      }}
      label={label}
      onChange={(e) => {
        if (
          selectedCompanyId &&
          e.target.value !== selectedCompany?.fields.name &&
          e.target.value !== selectedCompanyId
        ) {
          // Unset the selected company when a users starts typing a new value.
          onChange(null);
          setValue(null);
        }
        setInputValue(e.target.value);
      }}
      value={inputValue}
      variant="outlined"
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
}
